import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useDispatch, useSelector } from 'react-redux';
import { makeid } from 'utils/helpers/makeid';
import { PlatformCodeEnums } from 'infrastructure/enums';

import { getSmsDashboardConversationsKey } from './useConversations';
import { showResult } from '../utils';
import { getSmsDashboardMessagesKey } from './useMessages';

import type {
  ConversationType,
  IMessagesListPages,
} from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  type: ConversationType;
  read: string;
  closed: string;
  lastMessageDate: string;
  searchQuery: string;
  onSuccess: () => void;
}

interface FnProps {
  conversationId: number;
  spam: boolean;
}

const markConversationFn = async ({ conversationId, spam }: FnProps) => {
  const API_CLOSE_REOPEN_CONVERSATION = `core/conversations/spam`;

  const { data, error } = await dataService.createList(
    API_CLOSE_REOPEN_CONVERSATION,
    {
      conversationId,
      spam,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useMarkConversationAsSpam = (props: Props) => {
  const queryClient = useQueryClient();
  const { currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: markConversationFn,
    onSuccess(data) {
      const messagesKey = getSmsDashboardMessagesKey({
        conversationId: currentConversation.id,
        nextCursor: undefined,
        prevCursor: undefined,
        searchQuery: '',
        searchQueryDate: '',
      });
      const conversationsKey = getSmsDashboardConversationsKey({
        type: props.type,
        read: props.read,
        closed: props.closed,
        lastMessageDate: props.lastMessageDate,
        searchQuery: props.searchQuery,
      });

      queryClient.setQueryData<IMessagesListPages>(
        messagesKey,
        (oldMessages) => {
          if (!oldMessages) return;
          return {
            ...oldMessages,
            pages: [
              {
                ...oldMessages.pages[0],
                items: [
                  {
                    ...oldMessages.pages[0].items[0],
                    ...data,
                    id: makeid(10),
                    messageId: data.id,
                    updatedAt: new Date(),
                    createdAt: new Date(),
                    body: `Marked as ${data.spam ? 'Spam' : 'Not Spam'}`,
                    action: data.spam ? 'spam' : 'not_spam',

                    author: {
                      fullNameWithProfession: '',
                    },
                    authorGuid: '',
                    authorType: '',
                    from: PlatformCodeEnums.RPM,
                    to: PlatformCodeEnums.RPM,
                    patientClinicGuid: null,
                    read: false,
                    readAt: null,
                    readBy: null,
                    status: 'received',
                  },
                  ...oldMessages.pages[0].items,
                ],
              },
              ...oldMessages.pages,
            ],
          };
        },
      );
      props.onSuccess();
      changeCurrentConversation({
        ...currentConversation,
        spam: data.spam,
      });
      queryClient.invalidateQueries({ queryKey: conversationsKey });
    },
  });

  return {
    markConversationAsSpam: mutate,
    markConversationAsSpamLoading: isLoading,
    ...other,
  };
};
